import React, { useState, useRef, useEffect } from 'react';
import { buildZaloAuthorizeUrl } from '../utils/zaloAuth';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

// ═══════════════════════════════════════════════════════════════
// HuPath v2 Design Tokens (synced with Dashboard / AppHeader)
// ═══════════════════════════════════════════════════════════════
const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  cream: '#FFF8E7',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';
const V2 = '/images/dashboard-v2';

// ═══════════════════════════════════════════════════════════════
// Login translations
// ═══════════════════════════════════════════════════════════════
const STR = {
  vi: {
    welcomeBack: 'Chào mừng trở lại!',
    subDesktop: 'Đăng nhập để tiếp tục hành trình của bạn nhé!',
    subMobile: 'Đăng nhập nào!',
    email: 'Email',
    password: 'Mật khẩu',
    rememberMe: 'Ghi nhớ đăng nhập',
    forgot: 'Quên mật khẩu?',
    loggingIn: 'ĐANG ĐĂNG NHẬP...',
    cta: 'BẮT ĐẦU NGAY',
    newHere: 'Mới tham gia HuPath?',
    registerFree: 'Đăng ký miễn phí',
    registerShort: 'Đăng ký',
    tagline: 'Phiêu lưu học tiếng Anh cùng Hu!',
    titleLine1: 'Phiêu lưu học',
    titleLine2: 'tiếng Anh cùng Hu!',
    examLine: 'Cambridge YLE · Movers · Flyers · KET — biến thi cử thành phiêu lưu.',
    stat1: '🏆 50.000+ bé',
    stat2: '⭐ 4.9/5',
    stat3: '🎮 200+ trò chơi',
    loginFailed: 'Không đăng nhập được',
  },
  en: {
    welcomeBack: 'Welcome back!',
    subDesktop: 'Sign in to continue your journey!',
    subMobile: 'Let’s sign in!',
    email: 'Email',
    password: 'Password',
    rememberMe: 'Remember me',
    forgot: 'Forgot password?',
    loggingIn: 'SIGNING IN...',
    cta: 'LET’S GO',
    newHere: 'New to HuPath?',
    registerFree: 'Sign up free',
    registerShort: 'Sign up',
    tagline: 'Adventures in English with Hu!',
    titleLine1: 'Adventures in',
    titleLine2: 'English with Hu!',
    examLine: 'Cambridge YLE · Movers · Flyers · KET — turn exams into adventures.',
    stat1: '🏆 50,000+ kids',
    stat2: '⭐ 4.9/5',
    stat3: '🎮 200+ games',
    loginFailed: 'Login failed',
  },
};

// ═══════════════════════════════════════════════════════════════
// Language switcher (top-right) — vi / en dropdown
// ═══════════════════════════════════════════════════════════════
const LANGS = [
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

const LanguageSwitcher = ({ lang, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const current = LANGS.find(l => l.code === lang) || LANGS[0];
  return (
    <div ref={ref} style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000, fontFamily: FONT_BODY }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 14px', borderRadius: 999,
        background: HT.white, border: `2px solid ${HT.yellow}`,
        boxShadow: `0 3px 0 ${HT.yellowShadow}`,
        cursor: 'pointer', fontWeight: 700, fontSize: 13, color: HT.brownDark,
      }}>
        <span style={{ fontSize: 16, lineHeight: 1 }}>{current.flag}</span>
        <span>{current.label}</span>
        <span style={{ color: HT.textMuted, fontSize: 10 }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: HT.white, borderRadius: 14,
          border: `2px solid ${HT.yellow}`,
          boxShadow: SHADOW_MD, minWidth: 160, overflow: 'hidden',
        }}>
          {LANGS.map((l, i) => {
            const active = l.code === lang;
            return (
              <button key={l.code} type="button" onClick={() => { onChange(l.code); setOpen(false); }} style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '11px 16px', border: 'none',
                background: active ? HT.yellowBg : 'transparent',
                cursor: 'pointer', fontSize: 14, fontWeight: 700,
                color: active ? HT.brownDark : HT.text,
                fontFamily: FONT_BODY,
                borderTop: i > 0 ? `1px solid ${HT.border}` : 'none',
              }}>
                <span style={{ fontSize: 16 }}>{l.flag}</span>
                <span style={{ flex: 1, textAlign: 'left' }}>{l.label}</span>
                {active && <span style={{ color: HT.greenDark, fontWeight: 800 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Main Login — HuPath v2 Design System
// ═══════════════════════════════════════════════════════════════
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [zaloAppId, setZaloAppId] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/login/zalo/config`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.enabled) setZaloAppId(d.app_id); })
      .catch(() => {});
  }, []);
  const loginWithZalo = async () => {
    if (!zaloAppId) return;
    window.location.href = await buildZaloAuthorizeUrl(zaloAppId);
  };
  const [rememberMe, setRememberMe] = useState(true);
  const navigate = useNavigate();
  const { lang, toggleLang } = useLanguage();
  const t = STR[lang] || STR.vi;
  const setLang = (next) => { if (next !== lang) toggleLang(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    const details = { grant_type: 'password', username, password, scope: '', client_id: '', client_secret: '' };
    const formBody = Object.keys(details).map(k => encodeURIComponent(k) + '=' + encodeURIComponent(details[k])).join('&');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/login/access-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8', 'accept': 'application/json' },
        body: formBody,
      });
      if (!res.ok) { const ed = await res.json(); throw new Error(ed.detail || 'Login failed'); }
      const data = await res.json();
      localStorage.setItem('accessToken', data.access_token);
      const userRes = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${data.access_token}`, 'accept': 'application/json' },
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        localStorage.setItem('userFullName', userData.full_name || userData.email || '');
        localStorage.setItem('hasCompletedPlacement', userData.has_completed_placement ? 'true' : 'false');
        localStorage.setItem('userAvatar', userData.avatar || '');
        navigate(userData.has_completed_placement ? '/dashboard' : '/onboarding');
        return;
      }
      navigate('/dashboard');
    } catch (err) { setError(err.message || t.loginFailed); }
    finally { setIsLoading(false); }
  };

  // ─── Form (shared mobile + desktop) ────────────────────────────
  const renderForm = (isDesktop) => (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
      {isDesktop ? (
        <>
          <div style={{ fontSize: 36, fontWeight: 700, color: HT.text, letterSpacing: -1, fontFamily: FONT_DISPLAY }}>{t.welcomeBack}</div>
          <div style={{ fontSize: 15, color: HT.textSoft, marginTop: 8, marginBottom: 32, fontFamily: FONT_BODY }}>{t.subDesktop}</div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 22, fontWeight: 700, color: HT.text, fontFamily: FONT_DISPLAY }}>{t.welcomeBack}</div>
          <div style={{ fontSize: 13, color: HT.textSoft, marginBottom: 16, fontFamily: FONT_BODY }}>{t.subMobile}</div>
        </>
      )}

      {/* email */}
      {isDesktop && (
        <label style={{ fontSize: 13, fontWeight: 700, color: HT.text, marginBottom: 8, display: 'block', fontFamily: FONT_BODY, marginTop: 8 }}>
          {t.email}
        </label>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: isDesktop ? 12 : 10,
        background: HT.white, border: `2px solid ${HT.yellow}`,
        borderRadius: 999, padding: isDesktop ? '14px 20px' : '12px 16px',
        marginBottom: isDesktop ? 16 : 10,
        marginTop: isDesktop ? 0 : 8,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: isDesktop ? 20 : 18, color: HT.textMuted }}>mail</span>
        <input type="email" value={username} onChange={e => setUsername(e.target.value)}
          placeholder="be.minh@hupath.vn"
          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontFamily: FONT_BODY, fontSize: isDesktop ? 16 : 15, color: HT.text, fontWeight: 600 }}
        />
      </div>

      {/* password */}
      {isDesktop && (
        <label style={{ fontSize: 13, fontWeight: 700, color: HT.text, marginBottom: 8, display: 'block', fontFamily: FONT_BODY }}>{t.password}</label>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: isDesktop ? 12 : 10,
        background: HT.white, border: `2px solid ${HT.yellow}`,
        borderRadius: 999, padding: isDesktop ? '14px 20px' : '12px 16px',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: isDesktop ? 20 : 18, color: HT.textMuted }}>lock</span>
        <input type={isPasswordVisible ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
          placeholder="••••••••"
          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontFamily: FONT_BODY, fontSize: isDesktop ? 16 : 15, color: HT.text, fontWeight: 700,
            letterSpacing: isPasswordVisible ? 0 : 3 }}
        />
        <button type="button" onClick={() => setIsPasswordVisible(!isPasswordVisible)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
          <span className="material-symbols-outlined" style={{ fontSize: isDesktop ? 20 : 18, color: HT.textMuted }}>
            {isPasswordVisible ? 'visibility_off' : 'visibility'}
          </span>
        </button>
      </div>

      {/* Remember + Forgot */}
      {isDesktop ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 24 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: HT.text, cursor: 'pointer', fontFamily: FONT_BODY }}>
            <div onClick={() => setRememberMe(!rememberMe)} style={{
              width: 20, height: 20, borderRadius: 6,
              background: rememberMe ? HT.yellow : HT.white,
              border: rememberMe ? 'none' : `1px solid ${HT.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              transition: 'all 0.15s',
            }}>
              {rememberMe && <span className="material-symbols-outlined" style={{ fontSize: 14, color: HT.text, fontWeight: 700 }}>check</span>}
            </div>
            {t.rememberMe}
          </label>
          <button type="button" style={{ background: 'transparent', border: 'none', fontSize: 13, color: HT.coralDark, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY }}>{t.forgot}</button>
        </div>
      ) : (
        <div style={{ textAlign: 'right', marginTop: 8, marginBottom: 16 }}>
          <button type="button" style={{ background: 'transparent', border: 'none', fontSize: 12, color: HT.coralDark, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY }}>{t.forgot}</button>
        </div>
      )}

      {/* error */}
      {error && (
        <div style={{ padding: '10px 14px', background: HT.pinkBg, border: `1px solid ${HT.coral}`, borderRadius: 12, color: HT.coralDark, fontSize: 13, fontWeight: 700, marginBottom: 14, fontFamily: FONT_BODY }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 6 }}>warning</span>
          {error}
        </div>
      )}

      {/* primary CTA */}
      <button type="submit" disabled={isLoading} style={{
        width: '100%', padding: isDesktop ? '16px' : '14px',
        border: 'none', background: HT.coralDark, color: HT.white,
        fontFamily: FONT_BODY, fontSize: isDesktop ? 18 : 17, fontWeight: 700,
        borderRadius: 999, boxShadow: `0 4px 0 ${HT.coralPress}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        cursor: isLoading ? 'wait' : 'pointer', opacity: isLoading ? 0.75 : 1,
        transition: 'transform 0.1s, box-shadow 0.1s',
      }}>
        {isLoading ? t.loggingIn : <>{t.cta} <span className="material-symbols-outlined" style={{ fontSize: isDesktop ? 18 : 16 }}>play_arrow</span></>}
      </button>

      {/* Đăng nhập với Zalo — chỉ hiện khi backend đã cấu hình app */}
      {zaloAppId && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 10px',
            color: HT.textMuted, fontSize: 12, fontWeight: 700, fontFamily: FONT_BODY,
          }}>
            <div style={{ flex: 1, height: 1, background: HT.border }} />
            hoặc
            <div style={{ flex: 1, height: 1, background: HT.border }} />
          </div>
          <button type="button" onClick={loginWithZalo} style={{
            width: '100%', padding: isDesktop ? '14px' : '13px',
            border: 'none', background: '#0068FF', color: '#fff',
            fontFamily: FONT_BODY, fontSize: isDesktop ? 16 : 15, fontWeight: 700,
            borderRadius: 999, boxShadow: '0 4px 0 #0047B3',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            cursor: 'pointer',
          }}>
            <span style={{
              width: 24, height: 24, borderRadius: 6, background: '#fff', color: '#0068FF',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 900, fontSize: 13, fontFamily: FONT_BODY, flexShrink: 0,
            }}>Z</span>
            Đăng nhập với Zalo
          </button>
        </>
      )}

      {isDesktop && (
        <div style={{ textAlign: 'center', fontSize: 14, color: HT.textSoft, marginTop: 28, fontWeight: 600, fontFamily: FONT_BODY }}>
          {t.newHere}{' '}
          <button type="button" onClick={() => navigate('/register')} style={{
            background: 'transparent', border: 'none', color: HT.coralDark, fontWeight: 700,
            textDecoration: 'underline',
            cursor: 'pointer', fontFamily: FONT_BODY, fontSize: 14,
          }}>{t.registerFree}</button>
        </div>
      )}
    </form>
  );

  // Logo pill — same style as AppHeader on the dashboard
  const LogoPill = ({ size = 'md' }) => {
    const isLg = size === 'lg';
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center',
        gap: 2, lineHeight: 1,
        fontFamily: FONT_DISPLAY, fontWeight: 700,
        fontSize: isLg ? 28 : 22,
        color: HT.greenDark,
        padding: isLg ? '4px 22px 4px 4px' : '3px 16px 3px 3px',
        background: HT.white,
        border: `2px solid ${HT.yellow}`,
        borderRadius: 999,
        boxShadow: `0 3px 0 ${HT.yellowShadow}`,
      }}>
        <img src={`${V2}/owl-mini.png`} alt="HuPath" style={{
          width: isLg ? 60 : 48, height: isLg ? 60 : 48,
          objectFit: 'contain', display: 'block', flexShrink: 0,
        }} />
        <span>HuPath</span>
      </div>
    );
  };

  return (
    <>
      <LanguageSwitcher lang={lang} onChange={setLang} />
      <div style={{ minHeight: '100vh', display: 'flex', fontFamily: FONT_BODY, color: HT.text, background: HT.cream }}>
        {/* Desktop */}
        <div className="hidden lg:flex w-full">
          <div style={{
            flex: 1.1,
            background: HT.cream,
            borderRight: `4px solid ${HT.yellow}`,
            position: 'relative', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            padding: '40px 50px 50px',
          }}>
            {/* Ribbon decorations — match AppHeader */}
            <img src={`${V2}/ribbon-left.png`} alt="" aria-hidden="true" style={{
              position: 'absolute', top: 0, left: 0,
              width: 160, height: 'auto', pointerEvents: 'none', zIndex: 0,
              filter: 'drop-shadow(0 2px 3px rgba(139,111,71,0.15))',
            }} />
            <img src={`${V2}/ribbon-left.png`} alt="" aria-hidden="true" style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 160, height: 'auto', transform: 'scaleX(-1) scaleY(-1)',
              pointerEvents: 'none', zIndex: 0, opacity: 0.7,
              filter: 'drop-shadow(0 2px 3px rgba(139,111,71,0.15))',
            }} />

            <div style={{ position: 'relative', zIndex: 2 }}>
              <LogoPill size="md" />
            </div>

            <div style={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
              {/* Login banner illustration (frame already baked into image) */}
              <img src="/login_banner.png" alt="HuPath owl mascot" style={{
                width: '100%', maxWidth: 480, height: 'auto',
                display: 'block', margin: '0 auto',
                filter: 'drop-shadow(0 8px 16px rgba(139,111,71,0.18))',
              }} />
              <div style={{ fontSize: 38, fontWeight: 700, color: HT.greenDark, marginTop: 28, letterSpacing: -1, lineHeight: 1.1, fontFamily: FONT_DISPLAY }}>
                {t.titleLine1}<br />{t.titleLine2}
              </div>
              <div style={{ fontSize: 15, color: HT.textSoft, marginTop: 12, fontWeight: 600, maxWidth: 420, margin: '12px auto 0', fontFamily: FONT_BODY }}>
                {t.examLine}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', position: 'relative', zIndex: 2 }}>
              {[
                { t: t.stat1, bg: HT.yellow },
                { t: t.stat2, bg: HT.white },
                { t: t.stat3, bg: HT.greenSoft },
              ].map((p, i) => (
                <div key={i} style={{
                  background: p.bg, border: `2px solid ${HT.yellow}`,
                  boxShadow: `0 3px 0 ${HT.yellowShadow}`,
                  padding: '8px 16px', borderRadius: 999,
                  fontSize: 13, fontWeight: 700, color: HT.brownDark, fontFamily: FONT_BODY,
                }}>{p.t}</div>
              ))}
            </div>
          </div>

          <div style={{
            flex: 0.9, padding: '60px 70px', background: HT.white,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            maxHeight: '100vh', overflowY: 'auto',
          }}>
            {renderForm(true)}
          </div>
        </div>

        {/* Mobile */}
        <div className="flex lg:hidden flex-col w-full" style={{
          background: HT.cream,
          position: 'relative', overflow: 'hidden',
        }}>
          <img src={`${V2}/ribbon-left.png`} alt="" aria-hidden="true" style={{
            position: 'absolute', top: 0, left: 0,
            width: 110, height: 'auto', pointerEvents: 'none', zIndex: 0,
            filter: 'drop-shadow(0 2px 3px rgba(139,111,71,0.15))',
          }} />
          <img src={`${V2}/ribbon-left.png`} alt="" aria-hidden="true" style={{
            position: 'absolute', top: 0, right: 0,
            width: 110, height: 'auto', transform: 'scaleX(-1)',
            pointerEvents: 'none', zIndex: 0,
            filter: 'drop-shadow(0 2px 3px rgba(139,111,71,0.15))',
          }} />

          <div style={{ padding: '64px 24px 8px', textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <LogoPill size="md" />
            <div style={{ fontSize: 13, color: HT.textSoft, marginTop: 14, fontWeight: 600, fontFamily: FONT_BODY }}>
              {t.tagline}
            </div>
          </div>

          <div style={{
            background: HT.white,
            margin: '16px 16px 0', borderRadius: 20, padding: 22,
            border: `2px solid ${HT.yellow}`,
            boxShadow: SHADOW_MD, flex: 1, position: 'relative', zIndex: 2,
          }}>
            {renderForm(false)}
          </div>

          <div style={{ textAlign: 'center', fontSize: 13, color: HT.textSoft, padding: '20px 0', fontWeight: 600, position: 'relative', zIndex: 2, fontFamily: FONT_BODY }}>
            {t.newHere}{' '}
            <button type="button" onClick={() => navigate('/register')} style={{
              background: HT.yellow, color: HT.brownDark, padding: '6px 16px', borderRadius: 999,
              border: 'none', cursor: 'pointer', fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13,
              boxShadow: `0 2px 0 ${HT.yellowShadow}`,
            }}>{t.registerShort}</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
